/**
 * Pure-API auth helpers — cookie-based session, no localStorage tokens.
 * Used by AuthContext to keep the provider focused on state/memoisation.
 *
 * Tokens live in httpOnly cookies (`lumyn_access`, `lumyn_refresh`) set by
 * the backend on /auth/verify-otp /auth/login /auth/refresh, cleared on
 * /auth/logout. The browser handles them — JS cannot read them, which is
 * the whole point (XSS-resistant).
 *
 * The only thing we still keep in localStorage is the org_slug (cosmetic,
 * non-secret) so a fresh tab can show org branding before /auth/me returns.
 */
import api, { setImpersonateRole } from '../api/client';

const ORG_SLUG_KEY = 'lumyn_org_slug';

// Dedupe in-flight refreshes — protects against React.StrictMode's double
// effect invocation, hot-reload, fast double-mounts, etc. Server rotates
// refresh tokens; two simultaneous requests would otherwise race.
let _inFlightRefresh = null;

export function applyAuthResponse(data) {
  // Cookies are set by the server response — nothing to persist client-side.
  // Stash the org_slug so subsequent reloads can theme before /me resolves.
  if (data?.user?.org_slug) {
    try { localStorage.setItem(ORG_SLUG_KEY, data.user.org_slug); } catch (_e) { /* private mode */ }
  }
  return {
    user: data.user,
    org: { org_id: data.user?.org_id, org_name: data.user?.org_name },
  };
}

export async function loginWithPasswordRequest(email, password) {
  const { data } = await api.post('/auth/login', { email, password });
  return data;
}

export async function sendOtpRequest(email) {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { data } = await api.post('/auth/send-otp', { email });
      return data;
    } catch (err) {
      if (attempt === 0) {
        console.warn('[Auth] OTP send failed, retrying...', err.response?.status);
        await new Promise(r => setTimeout(r, 1500));
        continue;
      }
      throw err;
    }
  }
  return null;
}

export async function verifyOtpRequest(email, otpCode, opts = {}) {
  const body = { email, otp_code: otpCode };
  if (opts.trustDevice) {
    body.trust_device = true;
    if (opts.deviceLabel) body.device_label = opts.deviceLabel;
  }
  const { data } = await api.post('/auth/verify-otp', body);
  return data;
}

export async function trustLoginRequest(email) {
  const { data } = await api.post('/auth/trust-login', { email });
  return data;
}

export async function logoutRequest() {
  try { await api.post('/auth/logout'); } catch (err) { console.error('Logout API call failed:', err); }
  setImpersonateRole(null);
  try { localStorage.removeItem(ORG_SLUG_KEY); } catch (_e) { /* noop */ }
  try { sessionStorage.removeItem('lumyn_impersonate_role'); } catch (_e) { /* noop */ }
}

async function _doRefresh() {
  try {
    // Server rotates the refresh cookie + sets a fresh access cookie.
    // No body — refresh_token is read from the httpOnly cookie.
    await api.post('/auth/refresh', {});
    const { data: userData } = await api.get('/auth/me');
    const slug = (() => { try { return localStorage.getItem(ORG_SLUG_KEY); } catch (_e) { return null; } })();
    return { user: userData, org: { org_id: userData.org_id, org_slug: slug } };
  } catch (err) {
    // 401 = no/expired refresh cookie → user is signed out, that's fine.
    if (err?.response?.status !== 401) console.error('Session refresh failed:', err);
    return null;
  }
}

export function refreshSessionRequest() {
  if (_inFlightRefresh) return _inFlightRefresh;
  _inFlightRefresh = _doRefresh().finally(() => { _inFlightRefresh = null; });
  return _inFlightRefresh;
}
